import type { i18n as I18nInstance } from "i18next";
import type { CommonMessages } from "./createI18n";
import type { LanguageCode } from "./site-config";
import { setLanguageMessages } from "./createI18n";

const DEFAULT_NAMESPACE = "common";
const localeCache = new Map<LanguageCode, Promise<CommonMessages>>();

async function loadDevLocaleMessages(language: LanguageCode): Promise<CommonMessages> {
  const { translations } = await import("./translations");

  return (
    translations[language]?.[DEFAULT_NAMESPACE]
    ?? translations.en[DEFAULT_NAMESPACE]
  ) as CommonMessages;
}

function getLocaleUrl(language: LanguageCode) {
  const version = import.meta.env.DEV
    ? Date.now().toString()
    : __RUGIRA_BUILD_ID__;

  return `/locales/${language}/${DEFAULT_NAMESPACE}.json?v=${encodeURIComponent(version)}`;
}

async function fetchLocaleMessages(language: LanguageCode): Promise<CommonMessages> {
  if (import.meta.env.DEV) {
    return loadDevLocaleMessages(language);
  }

  const response = await fetch(getLocaleUrl(language), {
    credentials: "same-origin",
    cache: "force-cache",
  });

  if (!response.ok) {
    throw new Error(`Failed to load locale ${language}: ${response.status}`);
  }

  return response.json() as Promise<CommonMessages>;
}

export async function loadLocaleMessages(language: LanguageCode): Promise<CommonMessages> {
  if (import.meta.env.DEV) {
    return fetchLocaleMessages(language);
  }

  const cached = localeCache.get(language);
  if (cached) {
    return cached;
  }

  const request = fetchLocaleMessages(language).catch(async (error) => {
    if (language !== "en") {
      return fetchLocaleMessages("en");
    }
    throw error;
  });

  localeCache.set(language, request);
  return request;
}

export async function ensureLanguageLoaded(instance: I18nInstance, language: LanguageCode) {
  if (instance.hasResourceBundle(language, DEFAULT_NAMESPACE)) {
    return;
  }

  const messages = await loadLocaleMessages(language);
  setLanguageMessages(instance, language, messages);
}
